import React, { useCallback, useEffect, useState } from 'react'
import isNil from 'lodash/isNil'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'
import memoizeOne from 'memoize-one'
import { safeParseInt } from '@/checkout-instore-legacy/utils/numericUtils'
import {
  NumberInput,
  Text,
  csx,
  NumberInputValue,
  IconArrowLeft,
  Flex,
} from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'
import * as CustomEvent from '@/checkout-instore-legacy/utils/CustomEvent'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import { getProductKey } from '@/checkout-instore-legacy/utils/ProductUtils'
import {
  listenLogisticsInfoChanges,
  unlistenLogisticsInfoChanges,
} from '@/checkout-instore-legacy/cache/utils'

import ProductCarousel from '../ProductCarousel'
import SKUSelector from '@/checkout-instore-legacy/components/Commons/SKUSelector'

import { getLogisticsInfo } from '@/checkout-instore-legacy/cache/CatalogCache'
import { getDefaultSeller } from '@/checkout-instore-legacy/seller/utils'

import Loader from '@/checkout-instore-legacy/components/Commons/Loader'
import ProductPageError from '../ProductPageError'
import { isCached } from '@/checkout-instore-legacy/cache/CacheUtils'
import * as PullToRefresh from '@/checkout-instore-legacy/utils/PullToRefresh'
import * as WebViewBridge from '@/checkout-instore-legacy/utils/WebViewBridge'

import {
  getProductSkuData,
  getRouteParameters,
  getSpecifications,
  getSkuSelectorProps,
  getStateFromProductStoreChange,
} from '@/checkout-instore-legacy/product/utils'
import {
  PlaceHolderPrice,
  ProductPageProps,
  ProductPageStateFromProductStore,
  SkuSelectorProps,
} from '@/checkout-instore-legacy/product/typings'
import { usePrevious } from '@/checkout-instore-legacy/hooks/usePrevious'
import ProductPagePrice from '../ProductPagePrice'
import ProductPageName from '../ProductPageName'
import ProductPageTopMessage from '../ProductPageTopMessage'
import ShippingInformation from '../ShippingInformation'
import {
  isItemAvailableToAddToCart,
  isItemAvailableToEditQuantity,
  isItemWithoutStock,
} from '@/checkout-instore-legacy/components/SearchPage/utils/index'
import {
  getSimulationLogisticsInfo,
  isPickUpAvailable,
  isDeliveryAvailable,
} from '@/checkout-instore-legacy/cart/simulation/utils'
import ProductInformation from '@/checkout-instore-legacy/components/Commons/ProductInformation/ProductInformation'
import { ShadowedComponents } from '@/checkout-instore-legacy/__instore-core__/extensibility/ShadowedComponents'
import Attachments from '../../../components/Attachments'
import { AttachmentsProvider } from '@/checkout-instore-legacy/components/Attachments/provider/AttachmentsProvider'
import { SimilarProductsSection } from '../ProductPageSimilarProducts/SimilarProductsSection'
import { ProductPageOffersSection } from '../ProductPageOffersSection'
import { Separator } from './Separator'
import { ShowTogetherProductsSection } from '../ProductPageShowTogether/ShowTogetherProductsSection'
import { ProductPageBuyActions } from '../ProductPageBuyActions'

export const ProductPage = ({
  routeParams,
  location,
  router,
  intl,
  productStore = {},
  loadProduct,
  loadProductSku,
  updateSku,
  allowManualPrice,
  term,
  setLoadingStock,
  stock,
  isLoadingStock,
  updateStock,
  isSimulationLoading,
  clearLogisticsInfoCache,
  stopLoading,
  updateCartAddressWithSimulationAddress,
  loadSkuSimulation,
  shouldSimulationRefresh,
  simulationRefreshed,
  loadInstallments,
  installments,
  isLoadingInstallments,
  attachments,
  loadAttachments,
}: ProductPageProps) => {
  const {
    skuId: skuIdUrlParam,
    productId: productIdUrlParam,
    quantity: quantityUrlParam,
    manualPrice: manualPriceUrlParam,
    previousPage,
  } = getRouteParameters(location, routeParams)

  const SKUSelectorContainerRef = React.createRef<HTMLDivElement>()
  const { sellWithoutStockInHands, enableAttachments } = getInstoreConfig()

  const productStoreState: ProductPageStateFromProductStore = getStateFromProductStoreChange(
    productIdUrlParam,
    skuIdUrlParam,
    productStore || {},
    intl
  )

  const [updatingProduct, setUpdatingProduct] = useState(
    productStoreState.updatingProduct
  )
  const [error, setError] = useState(productStoreState.error)
  const [errorWithCacheDisabled, setErrorWithCacheDisabled] = useState(
    productStoreState.errorWithCacheDisabled
  )
  const [product, setProduct] = useState(productStoreState.product)
  const [defaultProductImages, setDefaultProductImages] = useState(
    productStoreState.defaultProductImages
  )
  const [selectedVariations, setSelectedVariations] = useState(
    productStoreState.selectedVariations || {}
  )
  const [sku, setSku] = useState<Item | undefined>(productStoreState.sku)

  const [manualPrice, setManualPrice] = useState<number | null>(
    manualPriceUrlParam
  )
  const [quantity, setQuantity] = useState(quantityUrlParam)
  const [simulationQuantity, setSimulationQuantity] = useState(1)

  const [showTopMessage, setShowTopMessage] = useState(false)
  const [logisticsInfoCache, setLogisticsInfoCache] = useState<
    Record<string, LogisticsInfo>
  >({})
  const [placeHolderPrice, setPlaceHolderPrice] = useState<PlaceHolderPrice>()
  const [hasDiscount, setHasDiscount] = useState()
  const [allVariationsSelected, setAllVariationsSelected] = useState<boolean>()

  const prevProductStore = usePrevious(productStore)
  const prevProduct = usePrevious(product)
  const prevSku = usePrevious(sku)

  const {
    availableQuantity,
    hasSimulationData,
    showSkuSelector,
    productDescription,
    skuImages,
    skuVideos,
    skuIsAvailableAtInitialQuantity,
    isAllVariationsSelected,
  } = getProductSkuData(
    product,
    sku,
    defaultProductImages,
    allVariationsSelected,
    logisticsInfoCache
  )

  const productSpecifications = getSpecifications(
    sku,
    product,
    isAllVariationsSelected,
    intl
  )

  useEffect(() => {
    sku && availableQuantity > 0 && loadSkuSimulation(sku, simulationQuantity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSkuSimulation, simulationQuantity, sku])

  useEffect(() => {
    if (shouldSimulationRefresh) {
      clearLogisticsInfoCache()
      loadSkuSimulation(sku, simulationQuantity)
      simulationRefreshed()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSimulationRefresh])

  useEffect(() => {
    if (sku?.id) {
      loadAttachments(sku.id)
    }
  }, [loadAttachments, sku])

  useEffect(() => {
    const hasChangedProduct = product?.id !== productIdUrlParam

    if (!product || hasChangedProduct) {
      handleProductRefresh()
    } else {
      listenLogisticsInfoChanges(product.skus, updateLogisticsInfoCache)
    }

    if (skuIdUrlParam) {
      updateStock(skuIdUrlParam)
    }

    WebViewBridge.sendEvent({
      type: 'enablePullToRefresh',
    })

    WebViewBridge.listen(onReceiveWebViewBridgeEvent)

    CustomEvent.listenEvent('topbar.back', handleBack)

    return componentWillUnmount

    // componentDidMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdUrlParam])

  const componentWillUnmount = () => {
    updateCartAddressWithSimulationAddress()
    clearLogisticsInfoCache()

    PullToRefresh.unlisten()

    WebViewBridge.sendEvent({
      type: 'disablePullToRefresh',
    })

    WebViewBridge.unlisten(onReceiveWebViewBridgeEvent)
    CustomEvent.unlistenEvent('topbar.back', handleBack)
    unlistenLogisticsInfoChanges(product?.skus, updateLogisticsInfoCache)
  }

  useEffect(() => {
    if (productStore.products !== prevProductStore?.products) {
      onProductStoreSkuChange()
      onProductStoreChange()
    }

    if (!isNil(product)) {
      if (isEmpty(logisticsInfoCache)) {
        updateLogisticsInfoCache()
      }

      if (isNil(prevProduct)) {
        listenLogisticsInfoChanges(product.skus, updateLogisticsInfoCache)
      }
    }

    const prevStateSkuId = prevSku && get(prevSku, 'id')

    const shouldLoadStock =
      !isLoadingStock &&
      !isEmpty(stock) &&
      prevStateSkuId &&
      sku &&
      get(sku, 'id') !== prevStateSkuId

    if (shouldLoadStock) {
      setLoadingStock(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productStore,
    product,
    sku,
    stock,
    logisticsInfoCache,
    prevProductStore,
    prevProduct,
    prevSku,
    isLoadingStock,
    setLoadingStock,
  ])

  const updateLogisticsInfoCache = debounce(
    () => {
      if (!product) {
        return
      }

      const skusIds = product.skus.map((sku) => sku.id)

      getLogisticsInfo(skusIds).then(
        // @ts-expect-error. Correct logisticsInfo typing from CatalogCache
        (logisticsInfo: Record<string, LogisticsInfo> | undefined) => {
          if (logisticsInfo) {
            setLogisticsInfoCache(logisticsInfo)
            selectInitialVariations(logisticsInfo)
          }
          stopLoading()
        }
      )
    },
    500,
    { maxWait: 1000 }
  )

  const selectInitialVariations = (
    logisticsInfo: Record<string, LogisticsInfo>
  ) => {
    if (product) {
      if (isEmpty(selectedVariations)) {
        const props = memoizedProcessSkuSelectorProps({
          product,
          logisticsInfoCache: logisticsInfo || logisticsInfoCache,
          intl,
        })
        const singleVariations = getInitialVariations(props) || []
        if (singleVariations.length) {
          getAutoSelectVariations(singleVariations)
        }
      }
    }
  }

  const getInitialVariations = (skus: SkuSelectorProps) => {
    const variations = skus.variations
    const availableSkus = skus.skus.filter((sku) => sku.available)

    if (isEmpty(variations)) {
      return
    }
    const availableVariations = variations.map((variation) => {
      const value = Array.from(
        new Set(availableSkus.map((sku) => sku[variation.name]))
      )
      return {
        label: variation.name,
        value,
      }
    })
    return availableVariations
  }

  const [isOpenAboutProduct, setIsOpenAboutProduct] = useState(false)
  const [isOpenSpecifications, setIsOpenSpecifications] = useState(false)

  const getAutoSelectVariations = (
    variations: { label: string; value: any[] }[]
  ) => {
    const newSelectedVariations: Record<string, any> = {}

    variations.forEach(
      (variation) =>
        (newSelectedVariations[variation.label] =
          variation.value.length === 1 ? variation.value[0] : null)
    )

    if (
      newSelectedVariations &&
      !isEqual(selectedVariations, newSelectedVariations)
    ) {
      setSelectedVariations(newSelectedVariations)
    }
  }

  const memoizedProcessSkuSelectorProps = memoizeOne(getSkuSelectorProps)

  const onReceiveWebViewBridgeEvent = (event: Event) => {
    if (event.type === 'pullToRefresh') {
      handleProductRefresh()
    }
  }

  const enablePullToRefresh = (element: HTMLElement | null) => {
    if (PullToRefresh.isAvailable() && element) {
      PullToRefresh.listen(element, handleProductRefresh)
    }
  }

  const handleBack = (event: CustomEvent.CustomEventType) => {
    event.preventDefault()
    const { router, location } = event.data
    const { state } = location
    const isTermNumber = term && safeParseInt(term)

    if (state && isTermNumber) {
      return router.replace({
        term: null,
        pathname: '/search',
      })
    }

    if (state) {
      return router.replace({
        pathname: '/search',
      })
    }

    if (location.query.skippedResults) {
      return router.replace({
        term: null,
        pathname: '/search',
      })
    }

    router.goBack()
  }

  const onProductStoreSkuChange = useCallback(() => {
    const sku = get(productStore, ['skuSimulationCache', skuIdUrlParam])
    sku && setSku(sku)
  }, [productStore, skuIdUrlParam])

  const onProductStoreChange = useCallback(() => {
    const currentProduct = product

    const newProduct = get(productStore, [
      'products',
      productIdUrlParam,
      'data',
    ])

    const productNeedToRefresh =
      getProductKey(newProduct, logisticsInfoCache) !==
      getProductKey(currentProduct, logisticsInfoCache)

    if (!currentProduct || productNeedToRefresh || updatingProduct) {
      let productStoreState: ProductPageStateFromProductStore = getStateFromProductStoreChange(
        productIdUrlParam,
        skuIdUrlParam,
        productStore,
        intl
      )

      if (productStoreState.updatingProduct) {
        productStoreState = handleUpdatedResult(productStoreState)
      }

      const hasDiscount = get(productStore, 'hasDiscount')

      setUpdatingProduct(productStoreState.updatingProduct)
      setError(productStoreState.error)
      setErrorWithCacheDisabled(productStoreState.errorWithCacheDisabled)
      setProduct(productStoreState.product)
      setDefaultProductImages(productStoreState.defaultProductImages)
      setSelectedVariations(productStoreState.selectedVariations)
      setSku(productStoreState.sku)
      setHasDiscount(hasDiscount)
    }
  }, [
    intl,
    logisticsInfoCache,
    product,
    productIdUrlParam,
    productStore,
    skuIdUrlParam,
    updatingProduct,
  ])

  const handleUpdatedResult = (state: ProductPageStateFromProductStore) => {
    const { errorWithCacheDisabled, product } = state

    let newState = {}

    if (product) {
      // This will update state with store data only if product was retrieved
      newState = state
    }

    if (!product || !isCached(product) || errorWithCacheDisabled) {
      PullToRefresh.stopLoading()
    }

    return {
      ...newState,
      updatingProduct: false,
      errorWithCacheDisabled,
    }
  }

  const handleVariationSelectionChange = ({
    sku,
    allVariationsSelected,
    selectedVariations,
    price,
  }: {
    sku: Item
    allVariationsSelected: boolean
    selectedVariations: any
    price: any
  }) => {
    const shouldUpdateStateAndStock = !Object.values(selectedVariations).some(
      isNil
    )
    if (shouldUpdateStateAndStock) {
      updateStock(get(sku, 'id'))
    }
    if (allVariationsSelected) {
      const { query } = location

      const seller = sku && getDefaultSeller(get(sku, 'sellers'))
      const availableQuantity =
        seller && get(seller, ['commertialOffer', 'availableQuantity'])

      const newQuantity = Math.min(quantity, availableQuantity)

      const callbackAfterLoad = (sku: Item) => {
        router.replace({
          pathname: `/product/${productIdUrlParam}`,
          query: {
            ...query,
            newQuantity,
            manualPrice,
            skuId: get(sku, 'id'),
          },
        })

        setSku(sku)
        setQuantity(newQuantity)
        setAllVariationsSelected(allVariationsSelected)
        setSelectedVariations(selectedVariations)
        setPlaceHolderPrice(price)
      }
      const skuId = get(sku, 'id')
      const cachedSku = get(productStore, ['skuSimulationCache', skuId])
      if (hasDiscount && !cachedSku) {
        loadProductSku(product, get(sku, 'id'), callbackAfterLoad)
      } else {
        cachedSku && updateSku(cachedSku)
        callbackAfterLoad(cachedSku || sku)
      }
    } else {
      setSku(sku)
      setAllVariationsSelected(allVariationsSelected)
      setSelectedVariations(selectedVariations)
      setPlaceHolderPrice(price)
    }
  }

  const callbackManualPriceChange = (manualPrice: number | null) => {
    setManualPrice(manualPrice)
    setShowTopMessage(manualPrice !== null && manualPrice !== undefined)
  }

  const handleRetry = () => {
    loadProduct(productIdUrlParam)
  }

  const handleProductRefresh = () => {
    setUpdatingProduct(true)
    loadProduct(productIdUrlParam, { disableCache: true })
  }

  if (error && !errorWithCacheDisabled) {
    return (
      <div className="product-alert">
        <ProductPageError onRetry={handleRetry} error={error} />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="flex flex-column flex-grow">
        <div className="relative flex-auto">
          <Loader loading />
        </div>
      </div>
    )
  }

  const handleChangeQuantity = (value: NumberInputValue) => {
    setSimulationQuantity(Number(value))
  }

  const quantitySelectorAvailable = isItemAvailableToEditQuantity({
    sellWithoutStockInHands,
    skuIsAvailableAtInitialQuantity,
  })

  const shouldHideNumericStepper = !quantitySelectorAvailable

  const { cartMaxSkuQuantity } = getInstoreConfig()
  const skuId = get(sku, 'id', '')

  const inStoreQuantity = isEmpty(stock)
    ? 0
    : get(stock, [skuId, 'checkedInStock'], 0)
  const otherStoresQuantity = isEmpty(stock)
    ? 0
    : get(stock, [skuId, 'otherStock'], 0)

  const hasCheckedInStock = (inStoreQuantity as number) > 0
  const logisticsInfo = getSimulationLogisticsInfo(
    logisticsInfoCache,
    skuId,
    simulationQuantity
  )

  const hasDeliveryStock = isDeliveryAvailable(
    logisticsInfo,
    simulationQuantity
  )
  const hasPickUpStock = isPickUpAvailable(logisticsInfo, simulationQuantity)
  const isMissingLogisticsInfoCache = !sku
  const isLoading =
    isLoadingStock || isSimulationLoading || isMissingLogisticsInfoCache

  const buyButtonAvailable = isItemAvailableToAddToCart({
    skuIsAvailableAtInitialQuantity,
    hasCheckedInStock,
    hasDeliveryStock,
    hasPickUpStock,
    sellWithoutStockInHands,
  })

  const withoutStock = isItemWithoutStock({
    quantity: simulationQuantity,
    availableQuantity,
    inStoreQuantity,
    otherStoresQuantity,
  })

  const cartMaxSkuQuantityNumber = parseInt(cartMaxSkuQuantity, 10)
  const maxValue = isNaN(cartMaxSkuQuantityNumber)
    ? undefined
    : cartMaxSkuQuantityNumber

  return (
    <AttachmentsProvider>
      <div
        id="ProductPage"
        className={csx({
          width: '100%',
          marginX: 'auto',
          background: '#F4F4F4', // [TODO]<theme>: $gray3
          paddingBottom: '6.25rem',
        })}
      >
        <div
          className={csx({
            background: '$white',
          })}
        >
          {!!previousPage && (
            <Flex
              className={csx({
                paddingY: '$space-5',
                paddingX: '$space-8',
                ':hover': { cursor: 'pointer' },
              })}
              onClick={() => router.goBack()}
            >
              <IconArrowLeft
                size="small"
                className={csx({ paddingX: '$space-1', color: '$gray40' })}
              />
              <Text tone="secondary">
                <FormattedMessage
                  id="breadcrumbLabel"
                  values={{
                    pageLabel: intl.formatMessage({
                      id: previousPage,
                    }),
                  }}
                />
              </Text>
            </Flex>
          )}
          <ProductPageTopMessage
            sku={sku}
            showMessage={showTopMessage}
            updatingProduct={updatingProduct}
            errorWithCacheDisabled={errorWithCacheDisabled}
            handleProductRefresh={handleProductRefresh}
          />
          <div
            className={csx({
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '96rem', // Parametrizar
              marginX: 'auto',
              '@desktop': {
                flexWrap: 'wrap',
                flexDirection: 'row',
              },
            })}
          >
            <div className="w-60-l w-100 flex justify-center ph7-ns pl9-l pr0-l">
              <ProductCarousel images={skuImages} videos={skuVideos} />
            </div>

            {ShadowedComponents?.ProductPage.BeforeProductName && (
              <div className="c-on-base ph5 ph7-ns mb2 dn-l mt5-s">
                <ShadowedComponents.ProductPage.BeforeProductName />
              </div>
            )}

            <div className="c-on-base ph5 ph7-ns t-heading-4 mb2 dn-l mt5-s">
              <ProductPageName product={product} sku={sku} />
            </div>

            <div
              className="w-40-l w-100 ph5 ph7-ns pl8-l pb7 pr9-l mt6-ns mt0-s"
              ref={enablePullToRefresh}
            >
              {ShadowedComponents?.ProductPage.BeforeProductName && (
                <div className="c-on-base dn db-l mb5">
                  <ShadowedComponents.ProductPage.BeforeProductName />
                </div>
              )}

              <div className="c-on-base dn db-l t-heading-4 mb5">
                <ProductPageName product={product} sku={sku} />
              </div>
              <ProductPagePrice
                product={product}
                sku={sku}
                allowManualPrice={allowManualPrice}
                initialManualPrice={manualPrice}
                placeHolderPrice={placeHolderPrice}
                logisticsInfoCache={logisticsInfoCache}
                isAllVariationsSelected={isAllVariationsSelected}
                skuIsAvailableAtInitialQuantity={
                  skuIsAvailableAtInitialQuantity
                }
                callbackManualPriceChange={callbackManualPriceChange}
                loadInstallments={loadInstallments}
                installments={installments}
                isLoadingInstallments={isLoadingInstallments}
              />
              <ProductPageOffersSection
                skus={productStoreState.product?.skus}
                selectedSku={sku}
              />
              <Separator />
              {product && !isEmpty(selectedVariations) && showSkuSelector ? (
                <div
                  className={`mt6 ${hasSimulationData ? '' : 'o-50'} `}
                  ref={SKUSelectorContainerRef}
                >
                  <SKUSelector
                    {...memoizedProcessSkuSelectorProps({
                      product,
                      logisticsInfoCache,
                      intl,
                    })}
                    key={`${getProductKey(
                      product,
                      logisticsInfoCache
                    )}-${hasSimulationData}`}
                    selectedVariations={selectedVariations}
                    onChange={handleVariationSelectionChange}
                    designTokens={{
                      skuSelectorContainer: `vtex-sku-selector__container flex flex-column mb6`,
                    }}
                  />
                </div>
              ) : null}

              <div
                className={csx({ marginBottom: '1.25rem', marginTop: '1rem' })}
              >
                <div className={csx({ marginBottom: '.5rem' })}>
                  <Text className={csx({ fontSize: 16, fontWeight: 600 })}>
                    <FormattedMessage id="quantity" />
                  </Text>
                </div>
                <div
                  className={`${shouldHideNumericStepper ? 'v-hidden' : ''}`}
                  id="quantity-selector-container"
                >
                  <NumberInput
                    min={1}
                    max={maxValue}
                    value={simulationQuantity}
                    onChange={handleChangeQuantity}
                    data-testid="product-page-quantity"
                  />
                </div>
              </div>
              <ProductPageBuyActions
                skuId={skuId}
                available={buyButtonAvailable}
                quantity={simulationQuantity}
                isLoading={isLoading}
                manualPrice={manualPrice}
                withoutStock={withoutStock}
                attachments={attachments}
              />
              <ShippingInformation
                skuId={skuId}
                logisticsInfo={logisticsInfo}
                quantity={simulationQuantity}
                isLoading={isLoading}
                showShippingInformation={isAllVariationsSelected}
              />
              <ProductInformation
                productInformation={[
                  {
                    title: 'aboutProductInformation',
                    contents: productDescription,
                    isOpen: isOpenAboutProduct,
                    setIsOpen: setIsOpenAboutProduct,
                  },
                  {
                    title: 'productSpecification',
                    contents: productSpecifications,
                    isOpen: isOpenSpecifications,
                    setIsOpen: setIsOpenSpecifications,
                  },
                ]}
              />
              {enableAttachments && <Attachments />}
            </div>
          </div>
        </div>
        {sku && (
          <div
            className={csx({
              '> *': {
                marginTop: '$space-8',
                paddingX: '$space-5',
                '@desktop': {
                  marginTop: '4rem',
                },
              },
            })}
          >
            <ShowTogetherProductsSection sku={{ ...sku, manualPrice }} />
          </div>
        )}
        <div
          className={csx({
            '> *': {
              marginTop: '$space-8',
              paddingX: '$space-5',
              '@desktop': {
                marginTop: '4rem',
              },
            },
          })}
        >
          <SimilarProductsSection productId={productIdUrlParam} />
        </div>
      </div>
    </AttachmentsProvider>
  )
}
